import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Radio,
  RadioGroup,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  AppBar,
  Toolbar,
  Container,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  Send,
  Business,
  LocationOn,
  Inventory,
  Category,
  Assignment,
  Description,
  AttachFile,
  CheckCircle,
  Warning,
  Error,
  Info,
  Edit,
  Save,
  Cancel,
  Add,
  Close,
  Download,
  Today,
  ExpandMore,
} from '@mui/icons-material';
import { useAuth } from 'react-oidc-context';
import appConfig from '../config/app';
import EnhancedCommentSystem from './EnhancedCommentSystem';
import MeasurementProtocolForm from './MeasurementProtocolForm';

// API service functions
const createApiService = (auth) => ({
  async getRMADetails(rmaId) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    const response = await fetch(`${appConfig.api.baseUrl}/rma/${rmaId}/details/?detailed=true`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  async getRMAComments(rmaId) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    const response = await fetch(`${appConfig.api.baseUrl}/rma/${rmaId}/comments/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  async addRMAComment(rmaId, comment) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    const response = await fetch(`${appConfig.api.baseUrl}/rma/${rmaId}/comments/add/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
      body: JSON.stringify({
        comment,
        visible_to_customer: true,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  async getRMAFieldIssues(rmaId) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    const response = await fetch(`${appConfig.api.baseUrl}/rma/${rmaId}/field-issues/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  async createFieldIssue(rmaId, fieldName, description, severity = 'medium') {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    const response = await fetch(`${appConfig.api.baseUrl}/rma/${rmaId}/field-issues/create/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
      body: JSON.stringify({
        field_name: fieldName,
        issue_description: description,
        severity,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  async resolveFieldIssue(issueId) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    const response = await fetch(`${appConfig.api.baseUrl}/rma/field-issues/${issueId}/resolve/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  async respondToFieldIssue(issueId, response) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    const apiResponse = await fetch(`${appConfig.api.baseUrl}/rma/field-issues/${issueId}/respond/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
      body: JSON.stringify({
        customer_response: response,
      }),
    });

    if (!apiResponse.ok) {
      throw new Error(`HTTP error! status: ${apiResponse.status}`);
    }

    return apiResponse.json();
  },

  async startRMAReview(rmaId) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    const response = await fetch(`${appConfig.api.baseUrl}/rma/${rmaId}/start-review/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  async completeRMAReview(rmaId, decision, finalComment = '') {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    const response = await fetch(`${appConfig.api.baseUrl}/rma/${rmaId}/complete-review/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
      body: JSON.stringify({
        decision,
        final_comment: finalComment,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  async updateRMARequest(rmaId, updateData) {
    console.log('Updating RMA with data:', updateData);
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    // Validate updateData
    if (!updateData || Object.keys(updateData).length === 0) {
      throw new Error('updateData is empty or invalid');
    }

    // Optionally, validate specific fields
    for (const [key, value] of Object.entries(updateData)) {
      if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) {
        throw new Error(`Invalid value for field ${key}`);
      }
    }

    const response = await fetch(`${appConfig.api.baseUrl}/rma/${rmaId}/`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
      body: JSON.stringify(updateData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Server error response:', errorData); // Log full error
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorData.error || 'Unknown error'}`);
    }

    return response.json();
  },

  async downloadAttachment(attachmentId) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    const response = await fetch(`${appConfig.api.baseUrl}/rma/attachments/${attachmentId}/download/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.blob();
  },
});

const fieldLabels = {
  company_name: 'Company Name',
  contact_person: 'Contact Person',
  email: 'Email',
  phone: 'Phone',
  address: 'Address',
  city: 'City',
  postal_code: 'Postal Code',
  country: 'Country',
  product_name: 'Product Name',
  product_code: 'Product Code',
  serial_number: 'Serial Number',
  purchase_date: 'Purchase Date',
  delivery_note_number: 'Delivery Note Number',
  description: 'Description',
};

const fieldIcons = {
  company_name: <Business />,
  contact_person: <Person />,
  email: <Email />,
  phone: <Phone />,
  address: <LocationOn />,
  city: <LocationOn />,
  postal_code: <LocationOn />,
  country: <LocationOn />,
  product_name: <Inventory />,
  product_code: <Category />,
  serial_number: <CheckCircle />,
  purchase_date: <Today />,
  delivery_note_number: <Assignment />,
  description: <Description />,
  default: <Info />,
};

// Editable Field Component
const EditableField = ({ fieldName, label, value, onUpdate, hasIssue, isEditing, onEditToggle }) => {
  const [editValue, setEditValue] = useState(value);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await onUpdate(fieldName, editValue);
      onEditToggle(fieldName, false);
    } catch (error) {
      console.error('Error updating field:', error);
      alert(`Error updating ${label}: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value);
    onEditToggle(fieldName, false);
  };

  const isMultiline = fieldName === 'description' || fieldName === 'address';

  return (
    <Box sx={{ p: 2, border: hasIssue ? '2px solid' : '1px solid', borderColor: hasIssue ? 'error.main' : 'grey.300', borderRadius: 1, bgcolor: hasIssue ? 'error.50' : 'background.paper', position: 'relative' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {fieldIcons[fieldName]}
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>
        {hasIssue && <Chip size="small" label="Issue" color="error" variant="outlined" />}
        <Box sx={{ ml: 'auto' }}>
          {isEditing ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton size="small" color="primary" onClick={handleSave} disabled={isLoading}>
                {isLoading ? <CircularProgress size={16} /> : <Save />}
              </IconButton>
              <IconButton size="small" color="default" onClick={handleCancel} disabled={isLoading}>
                <Cancel />
              </IconButton>
            </Box>
          ) : (
            <IconButton size="small" color="primary" onClick={() => onEditToggle(fieldName, true)}>
              <Edit />
            </IconButton>
          )}
        </Box>
      </Box>
      {isEditing ? (
        <TextField fullWidth multiline={isMultiline} rows={isMultiline ? 3 : 1} value={editValue} onChange={(e) => setEditValue(e.target.value)} variant="outlined" size="small" disabled={isLoading} sx={{ mt: 1 }} />
      ) : (
        <Typography variant="body1" sx={{ minHeight: '24px' }}>
          {value || '-'}
        </Typography>
      )}
    </Box>
  );
};

const FieldIssueCard = ({ issue, onResolve }) => {
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
      default: return 'default';
    }
  };

  return (
    <Accordion key={issue.id} sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {fieldIcons[issue.field_name]}
            <Typography variant="subtitle1">{fieldLabels[issue.field_name]}</Typography>
          </Box>
          <Chip size="small" label={issue.severity.toUpperCase()} color={getSeverityColor(issue.severity)} />
          {issue.resolved && <Chip size="small" label="Resolved" color="success" icon={<CheckCircle />} />}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ display: 'block', minHeight: '100px' }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Issue Description:
          </Typography>
          <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
            {issue.description || 'No description available'}
          </Typography>
        </Box>

        {issue.customer_response && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Customer Response:
            </Typography>
            <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
              {issue.customer_response}
            </Typography>
          </Box>
        )}

        {!issue.resolved && (
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircle />}
            onClick={() => onResolve(issue.id)}
          >
            Mark Resolved
          </Button>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const RMAReviewSystem = ({ rmaId, onBack }) => {
  const auth = useAuth();
  const [selectedRMA, setSelectedRMA] = useState(null);
  const [fieldIssues, setFieldIssues] = useState([]);
  const [newIssueDialog, setNewIssueDialog] = useState(false);
  const [newIssueField, setNewIssueField] = useState('');
  const [newIssueDescription, setNewIssueDescription] = useState('');
  const [newIssueSeverity, setNewIssueSeverity] = useState('medium');
  const [reviewDecision, setReviewDecision] = useState('');
  const [finalComment, setFinalComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', type: 'info' });
  const [tabValue, setTabValue] = useState(0);
  const [editingFields, setEditingFields] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const apiService = createApiService(auth);

  const showNotification = (message, type = 'info') => {
    setNotification({ open: true, message, type });
    setTimeout(() => setNotification({ ...notification, open: false }), 5000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted': return 'primary';
      case 'under_review': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'needs_info': return 'info';
      case 'return_to_producer': return 'warning';
      case 'return_to_customer': return 'success';
      case 'in_progress': return 'info';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const handleFieldUpdate = async (fieldName, newValue) => {
    try {
      if (newValue === undefined || newValue === null) {
        throw new Error('Invalid value');
      }

      const requiredFields = [
        'company_name', 'address', 'city', 'postal_code', 'country',
        'contact_person', 'product_code', 'product_name', 'serial_number',
        'description', 'email'
      ];

      for (const field of requiredFields) {
        if (!selectedRMA[field]) {
          throw new Error(`${fieldLabels[field] || field} is required but missing in RMA data`);
        }
      }

      const updateData = {
        [fieldName]: newValue,
        company_name: selectedRMA.company_name,
        address: selectedRMA.address,
        city: selectedRMA.city,
        postal_code: selectedRMA.postal_code,
        country: selectedRMA.country,
        contact_person: selectedRMA.contact_person,
        product_code: selectedRMA.product_code,
        product_name: selectedRMA.product_name,
        serial_number: selectedRMA.serial_number,
        description: selectedRMA.description,
        email: selectedRMA.email,
        conditions_accepted: selectedRMA.conditions_accepted,
        gdpr_accepted: selectedRMA.gdpr_accepted
      };

      await apiService.updateRMARequest(rmaId, updateData);
      setSelectedRMA(prev => ({
        ...prev,
        [fieldName]: newValue,
        updated_at: new Date().toISOString(),
      }));
      setHasUnsavedChanges(false);
      showNotification(`${fieldLabels[fieldName]} updated successfully`, `success`);
    } catch (error) {
      console.error('Error updating field:', error);
      let errorMessage = `Error updating ${fieldLabels[fieldName]}: ${error.message}`;
      if (error.message.includes('HTTP error') && error.message.includes('address is required')) {
        errorMessage = `Cannot update ${fieldLabels[fieldName]}: Address is required`;
      }
      showNotification(errorMessage, 'error');
      throw error;
    }
  };

  const handleEditToggle = (fieldName, isEditing) => {
    setEditingFields(prev => ({
      ...prev,
      [fieldName]: isEditing,
    }));
  };

  const handleSaveAllChanges = async () => {
    setLoading(true);
    try {
      await Promise.all(
        Object.entries(editingFields)
          .filter(([, isEditing]) => isEditing)
          .map(([fieldName]) => handleFieldUpdate(fieldName, selectedRMA[fieldName]))
      );
      setHasUnsavedChanges(false);
      showNotification('All changes saved successfully!', 'success');
    } catch (error) {
      showNotification('Error saving changes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddFieldIssue = async () => {
    if (!newIssueField || !newIssueDescription) return;

    try {
      const newIssue = await apiService.createFieldIssue(rmaId, newIssueField, newIssueDescription, newIssueSeverity);
      const normalizedIssue = {
        ...newIssue,
        description: newIssue.issue_description || newIssue.description || newIssueDescription,
      };
      setFieldIssues([...fieldIssues, normalizedIssue]);
      setNewIssueDialog(false);
      setNewIssueField('');
      setNewIssueDescription('');
      setNewIssueSeverity('medium');
      showNotification('Field issue added successfully', 'success');
      await loadData();
    } catch (error) {
      showNotification('Error adding field issue', 'error');
    }
  };

  const handleResolveIssue = async (issueId) => {
    try {
      await apiService.resolveFieldIssue(issueId);
      setFieldIssues(fieldIssues.map(issue => (issue.id === issueId ? { ...issue, resolved: true } : issue)));
      showNotification('Issue resolved successfully', 'success');
    } catch (error) {
      showNotification('Error resolving issue', 'error');
    }
  };

  const handleDownloadAttachment = async (attachmentId, filename) => {
    try {
      const blob = await apiService.downloadAttachment(attachmentId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      showNotification('Error downloading attachment', 'error');
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [rmaData, fieldIssuesData] = await Promise.all([
        apiService.getRMADetails(rmaId),
        apiService.getRMAFieldIssues(rmaId),
      ]);

      setSelectedRMA(rmaData);
      setFieldIssues(
        Array.isArray(fieldIssuesData?.field_issues)
          ? fieldIssuesData.field_issues.map(issue => ({
              ...issue,
              description: issue.issue_description || issue.description || 'No description available',
            }))
          : []
      );
    } catch (error) {
      showNotification(`Error loading data: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (rmaId) {
      loadData();
    }
  }, [rmaId]);

  const unresolved = fieldIssues.filter(issue => !issue.resolved);
  const resolved = fieldIssues.filter(issue => issue.resolved);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedRMA) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">RMA data not found</Alert>
      </Box>
    );
  }

  const handleSubmitReview = async () => {
    if (!reviewDecision) {
      showNotification('Please select a review decision', 'error');
      return;
    }

    setLoading(true);
    try {
      await apiService.completeRMAReview(rmaId, reviewDecision, finalComment);
      showNotification('Review submitted successfully', 'success');
      await loadData();
      setReviewDecision('');
      setFinalComment('');
    } catch (error) {
      showNotification(`Error submitting review: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      <AppBar position="static" elevation={1} sx={{ bgcolor: '#126F42' }}>
        <Toolbar>
          {onBack && (
            <IconButton edge="start" color="inherit" onClick={onBack} sx={{ mr: 2 }}>
              <Close />
            </IconButton>
          )}
          <Assignment sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            RMA Review System - {selectedRMA.rma_number}
          </Typography>

          {hasUnsavedChanges && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={loading ? <CircularProgress size={16} /> : <Save />}
              onClick={handleSaveAllChanges}
              disabled={loading}
              sx={{ mr: 2 }}
            >
              {loading ? 'Saving...' : 'Save All Changes'}
            </Button>
          )}

          <Chip
            label="Serviceman View"
            color="secondary"
            sx={{ ml: 2 }}
          />
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        {notification.open && (
          <Alert severity={notification.type} sx={{ mb: 2 }} onClose={() => setNotification({ ...notification, open: false })}>
            {notification.message}
          </Alert>
        )}

        {hasUnsavedChanges && (
          <Alert severity="warning" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={handleSaveAllChanges} startIcon={<Save />}>Save Now</Button>}>
            You have unsaved changes. Don't forget to save your work.
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h4" gutterBottom>
                  {selectedRMA.rma_number}
                </Typography>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {selectedRMA.company_name} - {selectedRMA.contact_person}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {selectedRMA.product_name} ({selectedRMA.product_code})
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', alignItems: 'center' }}>
                  <Chip label={selectedRMA.status.replace('_', ' ').toUpperCase()} color={getStatusColor(selectedRMA.status)} size="large" />
                  <Badge badgeContent={unresolved.length} color="error">
                    <Warning />
                  </Badge>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="RMA Details" />
            <Tab label={`Field Issues (${unresolved.length})`} />
            <Tab label="Comments" />
            <Tab label="Review Decision" />
            <Tab label="Measurement Protocol Form" />
          </Tabs>
        </Paper>

        {tabValue === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">RMA Information</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip size="small" label="Click edit icons to modify fields" color="info" variant="outlined" />
                    </Box>
                  </Box>
                  <Grid container spacing={2}>
                    {Object.entries(fieldLabels).map(([fieldName, label]) => {
                      const value = selectedRMA[fieldName] || '';
                      const hasIssue = fieldIssues.some(issue => issue.field_name === fieldName && !issue.resolved);
                      const isEditing = editingFields[fieldName] || false;
                      return (
                        <Grid item xs={12} md={6} key={fieldName}>
                          <EditableField
                            fieldName={fieldName}
                            label={label}
                            value={value}
                            onUpdate={handleFieldUpdate}
                            hasIssue={hasIssue}
                            isEditing={isEditing}
                            onEditToggle={handleEditToggle}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Attachments ({selectedRMA.attachments?.length || 0})
                  </Typography>
                  <List>
                    {selectedRMA.attachments?.map((attachment) => (
                      <ListItem key={attachment.id}>
                        <ListItemIcon>
                          <AttachFile />
                        </ListItemIcon>
                        <ListItemText
                          primary={attachment.original_filename || attachment.filename}
                          secondary={`${Math.round((attachment.file_size || attachment.size) / 1024)} KB`}
                        />
                        <IconButton color="primary" onClick={() => handleDownloadAttachment(attachment.id, attachment.original_filename || attachment.filename)}>
                          <Download />
                        </IconButton>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>

              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Last Updated
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(selectedRMA.updated_at).toLocaleString()}
                  </Typography>
                  {hasUnsavedChanges && (
                    <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                      Changes pending save
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {tabValue === 1 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Field Issues ({fieldIssues.length})</Typography>
                    <Button variant="contained" startIcon={<Add />} onClick={() => setNewIssueDialog(true)}>
                      Add Issue
                    </Button>
                  </Box>

                  {unresolved.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" color="error" gutterBottom>
                        Unresolved Issues ({unresolved.length})
                      </Typography>
                      {unresolved.map((issue) => (
                        <FieldIssueCard
                          key={issue.id}
                          issue={issue}
                          onResolve={handleResolveIssue}
                        />
                      ))}
                    </Box>
                  )}

                  {resolved.length > 0 && (
                    <Box>
                      <Typography variant="h6" color="success.main" gutterBottom>
                        Resolved Issues ({resolved.length})
                      </Typography>
                      {resolved.map((issue) => (
                        <FieldIssueCard
                          key={issue.id}
                          issue={issue}
                          onResolve={handleResolveIssue}
                        />
                      ))}
                    </Box>
                  )}

                  {fieldIssues.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        No field issues found
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {tabValue === 2 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <EnhancedCommentSystem
                rmaId={rmaId}
                isServiceman={true}
                onNotification={showNotification}
              />
            </Grid>
          </Grid>
        )}

        {tabValue === 3 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Review Decision
                  </Typography>

                  <FormControl component="fieldset" sx={{ mb: 3 }}>
                    <RadioGroup value={reviewDecision} onChange={(e) => setReviewDecision(e.target.value)}>
                      <FormControlLabel value="approved" control={<Radio />} label="Approve RMA" />
                      <FormControlLabel value="rejected" control={<Radio />} label="Reject RMA" />
                    </RadioGroup>
                  </FormControl>

                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Final Comments"
                    value={finalComment}
                    onChange={(e) => setFinalComment(e.target.value)}
                    placeholder="Add any final comments about this RMA decision..."
                    sx={{ mb: 3 }}
                  />

                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleSubmitReview}
                    disabled={!reviewDecision || loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                  >
                    {loading ? 'Submitting...' : 'Submit Review'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {tabValue === 4 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <MeasurementProtocolForm
                rmaId={rmaId}
                onNotification={showNotification}
              />
            </Grid>
          </Grid>
        )}

        <Dialog open={newIssueDialog} onClose={() => setNewIssueDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Add Field Issue</DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
              <InputLabel>Field</InputLabel>
              <Select value={newIssueField} onChange={(e) => setNewIssueField(e.target.value)} label="Field">
                {Object.entries(fieldLabels).map(([fieldName, label]) => (
                  <MenuItem key={fieldName} value={fieldName}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Issue Description"
              value={newIssueDescription}
              onChange={(e) => setNewIssueDescription(e.target.value)}
              placeholder="Describe the issue with this field..."
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth>
              <InputLabel>Severity</InputLabel>
              <Select value={newIssueSeverity} onChange={(e) => setNewIssueSeverity(e.target.value)} label="Severity">
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNewIssueDialog(false)}>Cancel</Button>
            <Button onClick={handleAddFieldIssue} variant="contained" disabled={!newIssueField || !newIssueDescription}>
              Add Issue
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default RMAReviewSystem;